import { fetchVerbandsbeitraege, fetchVerbandsbeitrag, fetchZaehlblaetter, fetchZaehlblatt } from '@/api/gildeReports';
import { subOrganizationHierarchyQueryOptions } from '@/api/organizationsQueryOptions';
import { fetchReportPermissionsQueryOptions } from '@/api/permissionsQueryOptions';
import { availableGildeReportsQueryOptions } from '@/api/reportQueryOptions';
import { fetchReport, ReportDefinition } from '@/api/reports';
import { ApiErrorComponent } from '@/components/ApiErrorComponent';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArticleIcon from '@mui/icons-material/Article';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import { Accordion, AccordionDetails, AccordionSummary, Avatar, Button, List, ListItem, ListItemAvatar, ListItemButton, ListItemIcon, ListItemText, Typography } from '@mui/material';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Buffer } from 'buffer';
import { enqueueSnackbar } from 'notistack';
import { FaRegFileExcel, FaRegFilePdf } from 'react-icons/fa';

export const Route = createFileRoute('/_auth/gildeReports')({
    component: GildeReportsComponent,
    errorComponent: ({ error }) => {
        if (error) {
            return <ApiErrorComponent error={error as Error} />
        } else {
            return <ApiErrorComponent />
        }
    },
})

const exportBlob = (blob: Blob, filename: string) => {
    // Save the blob in a json file
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();

    setTimeout(() => {
        URL.revokeObjectURL(url);
    });
};

const reportVerbandsbeitraege = () => {
    fetchVerbandsbeitraege().then((response) => {
        if (response) {
            const base64 = response.data.base64 || ''
            // console.log(base64)
            const raw = Buffer.from(base64, 'base64')
            // console.log(raw)
            const blob = new Blob([raw], {
                type: 'application/zip',
            });
            exportBlob(blob, response.data.filename || 'Verbandsbeiträge.zip');
        }
    })
}

const reportVerbandsbeitrag = (uuid: string) => {
    fetchVerbandsbeitrag(uuid).then((response) => {
        if (response) {
            const base64 = response.data.base64 || ''
            // console.log(base64)
            const raw = Buffer.from(base64, 'base64')
            // console.log(raw)
            const blob = new Blob([raw], {
                type: 'application/zip',
            });
            exportBlob(blob, response.data.filename || 'Verbandsbeitrage.docx');
        }
    })
}

const reportZaehlblaetter = () => {
    fetchZaehlblaetter().then((response) => {
        if (response) {
            const base64 = response.data.base64 || ''
            // console.log(base64)
            const raw = Buffer.from(base64, 'base64')
            // console.log(raw)
            const blob = new Blob([raw], {
                type: 'application/zip',
            });
            exportBlob(blob, response.data.filename || 'Zählblätter.zip');
        }
    })
}

const reportZaehlblatt = (uuid: string) => {
    fetchZaehlblatt(uuid).then((response) => {
        if (response) {
            const base64 = response.data.base64 || ''
            // console.log(base64)
            const raw = Buffer.from(base64, 'base64')
            // console.log(raw)
            const blob = new Blob([raw], {
                type: 'application/zip',
            });
            exportBlob(blob, response.data.filename || 'Zählblatt.docx');
        }
    })
}

const report = (report: ReportDefinition) => {
    enqueueSnackbar(`Der Bericht "${report.name}" wird erstellt. Dies kann je nach Größe des Berichts einige Zeit dauern.`, { variant: 'info' });
    fetchReport(report.url).then((response) => {
        if (response) {
            const base64 = response.data.base64 || ''
            const raw = Buffer.from(base64, 'base64')
            const blob = new Blob([raw], {
                type: report.mimetype,
            });
            enqueueSnackbar(`Der Bericht "${report.name}" wurde erstellt und wird heruntergeladen.`, { variant: 'success' });
            exportBlob(blob, response.data.filename || (report.filename));
        }
    }).catch(() => {
        enqueueSnackbar(`Fehler beim Erstellen des Berichts "${report.name}"`, { variant: 'error' });
    })
}

function GildeReportsComponent() {

    const availableReportsQuery = useSuspenseQuery(availableGildeReportsQueryOptions())
    const availableReports = availableReportsQuery.data

    const reportPermissionsQuery = useSuspenseQuery(fetchReportPermissionsQueryOptions(['gilde_verbandsbeitrag', 'gilde_zaehlblatt']))
    const reportPermissions = reportPermissionsQuery.data

    const readVerbandsbeitrag = reportPermissions && reportPermissions.filter(p => p.detail === 'gilde_verbandsbeitrag' && p.permissions.some(a => a === 'read')).length > 0
    const readZaehlblatt = reportPermissions && reportPermissions.filter(p => p.detail === 'gilde_zaehlblatt' && p.permissions.some(a => a === 'read')).length > 0

    const subOrgQuery = useSuspenseQuery(subOrganizationHierarchyQueryOptions())
    const subOrg = subOrgQuery.data
    const gilden = subOrg.filter(g => g.organizationLevel == 'TROOP').filter(g => !g.dissolvedYear).sort((a, b) => (a.name || '').localeCompare(b.name || ''))

    return <>
        <Typography variant='h6' gutterBottom>Berichte</Typography>
        <List>
            {availableReports.map((reportDef) => (
                <ListItemButton key={reportDef.url} onClick={() => report(reportDef)}>
                    <ListItemAvatar>
                        <Avatar>
                            {'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' === reportDef.mimetype && <FaRegFileExcel />}
                            {'application/pdf' === reportDef.mimetype && <FaRegFilePdf />}
                        </Avatar>
                    </ListItemAvatar>
                    <ListItemText primary={reportDef.name} secondary={reportDef.description} />
                </ListItemButton>
            ))}
        </List>
        {readVerbandsbeitrag &&
            <>
                <Accordion defaultExpanded>
                    <AccordionSummary
                        expandIcon={<ArrowDropDownIcon />}
                        aria-controls="verbandsbeitrag-content"
                        id="verbandsbeitrag-header"
                    >
                        <Typography component="span">Vorschreibung Verbandsbeitrag</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Button startIcon={<FolderZipIcon />} fullWidth={true} style={{ justifyContent: "flex-start", textTransform: "none" }} size="medium"
                            onClick={reportVerbandsbeitraege}>Alle Vorschreibungen
                        </Button>
                        <div style={{ height: '400px', width: '400px', overflowY: 'scroll' }}>
                            <List>
                                {gilden.map((gilde) => {
                                    return (
                                        <ListItem key={gilde.uuid}>
                                            <ListItemButton component="a" onClick={() => reportVerbandsbeitrag(gilde.uuid)}>
                                                <ListItemIcon><ArticleIcon /></ListItemIcon>
                                                <ListItemText primary={gilde.name} />
                                            </ListItemButton>
                                        </ListItem>
                                    )
                                })
                                }
                            </List>
                        </div>
                    </AccordionDetails>
                </Accordion>


            </>
        }
        {readZaehlblatt &&
            <>
                <Accordion>
                    <AccordionSummary
                        expandIcon={<ArrowDropDownIcon />}
                        aria-controls="zaehlblatt-content"
                        id="zaehlblatt-header"
                    >
                        <Typography component="span">Zählblatt</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Button startIcon={<FolderZipIcon />} fullWidth={true} style={{ justifyContent: "flex-start", textTransform: "none" }} size="medium"
                            onClick={reportZaehlblaetter}>Alle Zählblätter
                        </Button>
                        <div style={{ height: '400px', width: '400px', overflowY: 'scroll' }}>
                            <List>
                                {gilden.map((gilde) => {
                                    return (
                                        <ListItem key={gilde.uuid}>
                                            <ListItemButton component="a" onClick={() => reportZaehlblatt(gilde.uuid)}>
                                                <ListItemIcon><ArticleIcon /></ListItemIcon>
                                                <ListItemText primary={gilde.name} />
                                            </ListItemButton>
                                        </ListItem>
                                    )
                                })
                                }
                            </List>
                        </div>
                    </AccordionDetails>
                </Accordion>


            </>
        }
    </>
}
